import { useState, useEffect } from 'react';
import { X, Star, ExternalLink, Phone, Globe, MapPin, Coffee, Dog, Users, Sun, ChevronDown, ChevronUp, Wifi, DoorOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE = 'https://spotsvc.up.railway.app';

interface PlaceDetailDrawerProps {
  googlePlaceId: string | null;
  suggestionId: string | null;
  status: 'pending' | 'approved' | 'rejected' | null;
  onClose: () => void;
  onAction: (id: string, action: 'approved' | 'rejected') => void;
}

interface PlaceData {
  name: string;
  formatted_address: string | null;
  rating: number | null;
  price_level: string | null;
  website_uri: string | null;
  google_maps_uri: string | null;
  editorial_summary: string | null;
  business_status: string | null;
  phone_national: string | null;
  regular_hours: { weekdayDescriptions?: string[] } | null;
  photos: string[] | null;
  serves_coffee: boolean | null;
  outdoor_seating: boolean | null;
  wifi_available: boolean | null;
  allows_dogs: boolean | null;
  good_for_groups: boolean | null;
  restroom: boolean | null;
  serves_breakfast: boolean | null;
  serves_lunch: boolean | null;
  serves_brunch: boolean | null;
  dine_in: boolean | null;
  takeout: boolean | null;
  delivery: boolean | null;
}

const AMENITIES: { key: keyof PlaceData; label: string; icon: React.ReactNode }[] = [
  { key: 'serves_coffee', label: 'Coffee', icon: <Coffee className="w-3.5 h-3.5" /> },
  { key: 'outdoor_seating', label: 'Outdoor', icon: <Sun className="w-3.5 h-3.5" /> },
  { key: 'wifi_available', label: 'WiFi', icon: <Wifi className="w-3.5 h-3.5" /> },
  { key: 'allows_dogs', label: 'Dogs OK', icon: <Dog className="w-3.5 h-3.5" /> },
  { key: 'restroom', label: 'Restroom', icon: <DoorOpen className="w-3.5 h-3.5" /> },
  { key: 'good_for_groups', label: 'Groups', icon: <Users className="w-3.5 h-3.5" /> },
  { key: 'serves_breakfast', label: 'Breakfast', icon: <Coffee className="w-3.5 h-3.5" /> },
  { key: 'serves_lunch', label: 'Lunch', icon: <Coffee className="w-3.5 h-3.5" /> },
  { key: 'serves_brunch', label: 'Brunch', icon: <Coffee className="w-3.5 h-3.5" /> },
  { key: 'dine_in', label: 'Dine in', icon: <Coffee className="w-3.5 h-3.5" /> },
  { key: 'takeout', label: 'Takeout', icon: <Coffee className="w-3.5 h-3.5" /> },
];

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={cn('animate-pulse bg-muted rounded-lg', className)} />;
}

function Skeleton() {
  return (
    <div className="p-5 space-y-4">
      <SkeletonBlock className="h-7 w-3/4" />
      <SkeletonBlock className="h-4 w-1/3" />
      <SkeletonBlock className="h-4 w-full" />
      <SkeletonBlock className="h-4 w-2/3" />
      <SkeletonBlock className="h-24 w-full" />
      <div className="flex gap-2">
        <SkeletonBlock className="h-16 w-16" />
        <SkeletonBlock className="h-16 w-16" />
        <SkeletonBlock className="h-16 w-16" />
      </div>
    </div>
  );
}

export default function PlaceDetailDrawer({ googlePlaceId, suggestionId, status, onClose, onAction }: PlaceDetailDrawerProps) {
  const [place, setPlace] = useState<PlaceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoursOpen, setHoursOpen] = useState(false);

  useEffect(() => {
    if (!googlePlaceId) return;
    setPlace(null);
    setError(null);
    setLoading(true);
    fetch(`${API_BASE}/admin/google-place/${googlePlaceId}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setPlace(d.place))
      .catch(() => setError('Failed to load place details.'))
      .finally(() => setLoading(false));
  }, [googlePlaceId]);

  const isOpen = !!googlePlaceId;

  const amenities = place ? AMENITIES.filter(a => place[a.key] === true) : [];
  // Photos hidden — API now returns photo_references (tokens), not usable URLs
  const photos: string[] = [];
  const hours = place?.regular_hours?.weekdayDescriptions || [];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-foreground/30"
            onClick={onClose}
          />
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-card border-l border-input shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-input">
              <div className="flex-1 min-w-0">
                {loading ? (
                  <SkeletonBlock className="h-7 w-3/4" />
                ) : place ? (
                  <>
                    <h2 className="text-xl font-bold text-foreground truncate">{place.name}</h2>
                    {place.business_status && (
                      <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-success/15 text-success">
                        {place.business_status}
                      </span>
                    )}
                  </>
                ) : error ? (
                  <h2 className="text-xl font-bold text-foreground">Place Details</h2>
                ) : null}
              </div>
              <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground ml-2 flex-shrink-0">
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {loading && <Skeleton />}

              {error && (
                <div className="p-5">
                  <div className="bg-destructive/10 text-destructive text-sm rounded-xl px-4 py-3">{error}</div>
                </div>
              )}

              {place && !loading && (
                <div className="p-5 space-y-5">
                  {/* Address */}
                  {place.formatted_address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-foreground">{place.formatted_address}</p>
                        {place.google_maps_uri && (() => {
                          const cidMatch = place.google_maps_uri!.match(/cid=(\d+)/);
                          const href = cidMatch ? `https://maps.google.com/maps?cid=${cidMatch[1]}` : place.google_maps_uri!;
                          return (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1">
                              Open in Google Maps <ExternalLink className="w-3 h-3" />
                            </a>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Rating + Price */}
                  {(place.rating || place.price_level) && (
                    <div className="flex items-center gap-3">
                      {place.rating && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-warning fill-warning" />
                          <span className="text-sm font-semibold text-foreground">{place.rating}</span>
                        </div>
                      )}
                      {place.price_level && (
                        <span className="text-sm text-muted-foreground">{place.price_level}</span>
                      )}
                    </div>
                  )}

                  {/* Phone */}
                  {place.phone_national && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-foreground">{place.phone_national}</span>
                    </div>
                  )}

                  {/* Website */}
                  {place.website_uri && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      <a href={place.website_uri} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline truncate">
                        {place.website_uri.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                      </a>
                    </div>
                  )}

                  {/* Editorial summary */}
                  {place.editorial_summary && (
                    <p className="text-sm italic text-muted-foreground border-l-2 border-input pl-3">{place.editorial_summary}</p>
                  )}

                  {/* Opening hours */}
                  {hours.length > 0 && (
                    <div>
                      <button onClick={() => setHoursOpen(v => !v)} className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                        Opening hours
                        {hoursOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {hoursOpen && (
                        <ul className="mt-2 space-y-0.5">
                          {hours.map((h, i) => (
                            <li key={i} className="text-xs text-muted-foreground">{h}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Amenities */}
                  {amenities.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-2">Amenities</h4>
                      <div className="flex flex-wrap gap-2">
                        {amenities.map(a => (
                          <span key={a.key} className="inline-flex items-center gap-1.5 text-xs font-medium bg-muted text-muted-foreground px-2.5 py-1.5 rounded-lg">
                            {a.icon} {a.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Photos */}
                  {photos.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-2">Photos</h4>
                      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                        {photos.map((url, i) => (
                          <img key={i} src={url} alt="" className="w-28 h-20 rounded-lg object-cover flex-shrink-0" />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer — only for pending */}
            {status === 'pending' && place && !loading && suggestionId && (
              <div className="p-4 border-t border-input flex gap-2">
                <button
                  onClick={() => onAction(suggestionId, 'approved')}
                  className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-success text-primary-foreground hover:opacity-90"
                >
                  Approve
                </button>
                <button
                  onClick={() => onAction(suggestionId, 'rejected')}
                  className="flex-1 py-2.5 text-sm font-medium rounded-lg border border-destructive text-destructive hover:bg-destructive/10"
                >
                  Reject
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
